import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { DeleteLossKind, DeleteLossRow } from './deleteLossRows';
import { STATS_WINDOW_DAYS } from '../pageActions/useSmartLinkPageFacts';
import { Panel, PanelHead } from '../../music24/components/primitives/Panel';
import styles from './SMLDeleteLossList.module.scss';

interface SMLDeleteLossListProps {
  rows: DeleteLossRow[];
  shareUrl: string | null;
}

/**
 * What the deletion actually costs, itemised.
 *
 * The rows that fall on other people — fans left waiting for a track that
 * never arrives, an address already printed on a poster — are red. The rest
 * is the artist's own data, which is a loss but only theirs.
 */
const SMLDeleteLossListComponent: React.FC<SMLDeleteLossListProps> = ({ rows, shareUrl }) => {
  const intl = useIntl();

  const titleFor = (row: DeleteLossRow): string => {
    switch (row.kind) {
      case DeleteLossKind.presaves:
        return intl.formatMessage(
          {
            id: 'app.sml.delete.loss.presaves.title',
            defaultMessage: '{count, plural, one {# pre-save is cancelled} other {# pre-saves are cancelled}}',
          },
          { count: row.figure ?? 0 }
        );
      case DeleteLossKind.link:
        return intl.formatMessage({ id: 'app.sml.delete.loss.link.title', defaultMessage: 'The link stops working' });
      case DeleteLossKind.analytics:
        return intl.formatMessage({ id: 'app.sml.delete.loss.analytics.title', defaultMessage: 'Analytics deleted' });
      case DeleteLossKind.emails:
      default:
        return intl.formatMessage({
          id: 'app.sml.delete.loss.emails.title',
          defaultMessage: 'Email reminders deleted',
        });
    }
  };

  const detailFor = (row: DeleteLossRow): string => {
    switch (row.kind) {
      case DeleteLossKind.presaves:
        return intl.formatMessage({
          id: 'app.sml.delete.loss.presaves.text',
          defaultMessage: "Fans who asked for this track never receive it, and aren't told why.",
        });
      case DeleteLossKind.link:
        return shareUrl ?? '';
      case DeleteLossKind.analytics:
        return intl.formatMessage(
          {
            id: 'app.sml.delete.loss.analytics.text',
            defaultMessage: 'Views, service taps and where fans came from, over the last {days} days.',
          },
          { days: STATS_WINDOW_DAYS }
        );
      case DeleteLossKind.emails:
      default:
        return intl.formatMessage({
          id: 'app.sml.delete.loss.emails.text',
          defaultMessage: 'Addresses collected on this page only — your audience list is untouched.',
        });
    }
  };

  return (
    <Panel nested className={styles.card}>
      <PanelHead title={intl.formatMessage({ id: 'app.sml.delete.loss.head', defaultMessage: 'What goes with it' })} />
      {rows.map((row) => (
        <div key={row.kind} className={clsx(styles.row, row.isHard && styles.hard)}>
          <div className={styles.text}>
            <span className={clsx(styles.title, row.isHard && styles.titleHard)}>{titleFor(row)}</span>
            <span className={clsx(styles.detail, row.kind === DeleteLossKind.link && styles.mono)}>
              {detailFor(row)}
            </span>
          </div>
          {row.figure !== null && row.kind !== DeleteLossKind.presaves && (
            <span className={styles.figure}>{intl.formatNumber(row.figure)}</span>
          )}
        </div>
      ))}
    </Panel>
  );
};

export const SMLDeleteLossList = TypedMemo(SMLDeleteLossListComponent);
